import type { AiModelContent, AiModelId } from '@/constants/aiLeague/aiLeagueTypes';
import { AI_LEAGUE_SWAP_JUSTIFICATIONS } from '@/constants/aiLeague/swapJustifications';
import type { AiLeagueSwapsById } from '@/models/aiLeague';

function resolvedSwapJustification(modelId: AiModelId, record?: AiLeagueSwapsById[AiModelId]): string | null {
  const fromFirestore = record?.swapJustificationText?.trim();
  if (fromFirestore) {
    return fromFirestore;
  }
  const fromStatic = AI_LEAGUE_SWAP_JUSTIFICATIONS[modelId]?.trim();
  return fromStatic ?? null;
}

/** Merge Firestore swap records onto static AI model content. */
export function mergeAiLeagueModels(
  staticModels: AiModelContent[],
  swapsById: AiLeagueSwapsById,
): AiModelContent[] {
  return staticModels.map((model) => {
    const record = swapsById[model.id];
    if (!model.listValid) {
      return { ...model, listIndexSwapPair: null, swapJustificationText: null };
    }
    return {
      ...model,
      listIndexSwapPair: record?.listIndexSwapPair ?? null,
      swapJustificationText: resolvedSwapJustification(model.id, record),
    };
  });
}

/** 0-based row indices to highlight on the post-swap display list. */
export function swappedDisplayRankIndices(pair: [number, number] | null | undefined): Set<number> {
  if (!pair) {
    return new Set();
  }
  return new Set(pair);
}

export function swapJustificationForDisplay(
  text: string | null | undefined,
  placeholder: string,
): { text: string; isPlaceholder: boolean } {
  const trimmed = text?.trim();
  if (trimmed) {
    return { text: trimmed, isPlaceholder: false };
  }
  return { text: placeholder, isPlaceholder: true };
}

export function swapPairToRankLabel(pair: [number, number]): string {
  return `${pair[0] + 1} ↔ ${pair[1] + 1}`;
}

export function draftSwapFromSelection(selectedIndices: string[]): [number, number] | null {
  if (selectedIndices.length === 0) {
    return null;
  }
  if (selectedIndices.length !== 2) {
    return null;
  }
  const a = Number.parseInt(selectedIndices[0]!, 10);
  const b = Number.parseInt(selectedIndices[1]!, 10);
  if (Number.isNaN(a) || Number.isNaN(b) || a === b) {
    return null;
  }
  return a < b ? [a, b] : [b, a];
}

export function selectionFromSwapPair(pair: [number, number] | null | undefined): string[] {
  if (!pair) {
    return [];
  }
  return [pair[0].toString(), pair[1].toString()];
}

export function swapsEqual(
  a: [number, number] | null | undefined,
  b: [number, number] | null | undefined,
): boolean {
  if (a == null && b == null) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  return (a[0] === b[0] && a[1] === b[1]) || (a[0] === b[1] && a[1] === b[0]);
}

export function savedSwapsByModelId(
  swapsById: AiLeagueSwapsById,
  modelIds: AiModelId[],
): Record<AiModelId, [number, number] | null> {
  const out = {} as Record<AiModelId, [number, number] | null>;
  for (const id of modelIds) {
    out[id] = swapsById[id]?.listIndexSwapPair ?? null;
  }
  return out;
}

export function savedJustificationsByModelId(
  swapsById: AiLeagueSwapsById,
  modelIds: AiModelId[],
): Record<AiModelId, string> {
  const out = {} as Record<AiModelId, string>;
  for (const id of modelIds) {
    out[id] = swapsById[id]?.swapJustificationText?.trim() ?? '';
  }
  return out;
}
